import { openCheckoutModal, type CheckoutHandle } from "./modal";
import type {
  BorgaOptions,
  CheckoutOptions,
  CheckoutResult,
  PendingCheckout,
} from "./types";

const PUBLISHABLE_KEY_PREFIXES = ["pk_test_", "pk_live_"];

/** The checkout this instance currently has on screen, if any. */
interface ActiveCheckout {
  handle: CheckoutHandle | null;
  /** Close the modal and settle its promise as canceled. */
  cancel: () => void;
}

class Borga {
  private readonly opts: BorgaOptions;
  private active: ActiveCheckout | null = null;

  /**
   * The publishable key this instance was constructed with, if any.
   *
   * Nothing in this SDK authenticates with it today: an embedded checkout is
   * authenticated by the payment session id plus its one-time `client_secret`,
   * both minted by your server. The key is accepted (and validated) so that
   * existing integrations keep working and so it is available for planned
   * origin pairing and analytics.
   */
  readonly publishableKey?: string;

  /**
   * @param publishableKey Optional `pk_test_…` / `pk_live_…` key. See
   *   {@link Borga.publishableKey} — the session id and `client_secret` are
   *   what authenticate the checkout, so this may be omitted.
   * @param opts Optional API / checkout host overrides for local development.
   */
  constructor(publishableKey?: string, opts: BorgaOptions = {}) {
    if (publishableKey !== undefined && publishableKey !== null) {
      if (
        typeof publishableKey !== "string" ||
        !PUBLISHABLE_KEY_PREFIXES.some((p) => publishableKey.startsWith(p))
      ) {
        throw new Error(
          "new Borga(publishableKey): publishable key must start with pk_test_ or pk_live_. " +
            "It is optional — call new Borga() if you do not have one.",
        );
      }
      this.publishableKey = publishableKey;
    }
    this.opts = opts;
  }

  readonly checkout = {
    /**
     * Open the checkout modal for an embedded payment session.
     *
     * The return value is both the handle it has always been and a promise of
     * the {@link CheckoutResult}, so callbacks and `await` both work:
     *
     * ```ts
     * const checkout = borga.checkout.open({ sessionId, clientSecret });
     * checkout.close();
     *
     * const result = await borga.checkout.open({ sessionId, clientSecret });
     * if (result.status === "complete") console.log(result.paymentId);
     * ```
     *
     * The promise resolves for cancellation and payment errors too — it never
     * rejects — so `await` without a try/catch is safe. Only one checkout can
     * be open per instance: opening a second one closes the first (resolving
     * its promise as `{ status: "canceled" }`).
     */
    open: (options: CheckoutOptions): PendingCheckout =>
      this.openCheckout(options),
  };

  private openCheckout(options: CheckoutOptions): PendingCheckout {
    let resolve!: (result: CheckoutResult) => void;
    const promise = new Promise<CheckoutResult>((r) => {
      resolve = r;
    });

    let settled = false;
    const settle = (result: CheckoutResult) => {
      if (settled) return;
      settled = true;
      if (this.active === active) this.active = null;
      resolve(result);
    };

    const active: ActiveCheckout = {
      handle: null,
      cancel: () => {
        // close() runs the modal's own dismissal path, which fires onCancel
        // and therefore settles the promise. The settle() below is a
        // belt-and-braces no-op in that case.
        active.handle?.close();
        settle({ status: "canceled" });
      },
    };

    // One modal at a time: two overlays would share a host element and a
    // single scroll-lock restore, so dismiss whatever is still on screen.
    this.active?.cancel();

    let handle: CheckoutHandle;
    try {
      handle = openCheckoutModal({
        checkoutUrl: this.opts.checkoutUrl,
        ...options,
        onComplete: (result) => {
          settle({ status: "complete", paymentId: result.paymentId });
          options.onComplete?.(result);
        },
        onCancel: () => {
          settle({ status: "canceled" });
          options.onCancel?.();
        },
        onError: (err) => {
          settle({ status: "error", error: err });
          options.onError?.(err);
        },
      });
    } catch (err) {
      // Nothing was mounted (e.g. called outside the browser) — make sure we
      // do not leave a tracked checkout behind, then rethrow as before.
      settled = true;
      throw err;
    }

    active.handle = handle;
    // A modal that settled synchronously is already gone; don't track it.
    if (!settled) this.active = active;

    return Object.assign(promise, { close: active.cancel });
  }
}

// tsup's IIFE emits `var Borga = (() => <namespace>)()` — the namespace
// is `{ default: class }`, not the class itself, so `new window.Borga(...)`
// would throw. The tsup config has a footer that unwraps `.default` onto
// `window.Borga` after the IIFE closes; see apps/js/tsup.config.ts.
export default Borga;
export type { CheckoutHandle };
export type { CheckoutResult, PendingCheckout } from "./types";
